import NotesHistoryButton from '../../components/NotesHistoryButton';
import { useState, useEffect } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  Typography, TextField, IconButton, Autocomplete, Chip, Select, MenuItem, Paper,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import DraggableDialog from '../../components/DraggableDialog';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import { CopyId } from '../../components/CopyId';
import { flowsService, RuleCondition } from '../../services/flows.service';
import { offersService, Offer } from '../../services/offers.service';
import { landingPagesService, LandingPage } from '../../services/landing-pages.service';
import { COUNTRIES, COUNTRIES_MAP } from '../../utils/countries';

const CONDITION_FIELDS = [
  { value: 'country', label: 'Country' },
  { value: 'device_type', label: 'Device Type' },
  { value: 'os', label: 'OS' },
  { value: 'browser', label: 'Browser' },
  { value: 'language', label: 'Language' },
  { value: 'external_id_type', label: 'External ID Type' },
  { value: 'weekday', label: 'Weekday' },
  { value: 'v1', label: 'v1' }, { value: 'v2', label: 'v2' }, { value: 'v3', label: 'v3' },
  { value: 'v4', label: 'v4' }, { value: 'v5', label: 'v5' }, { value: 'v6', label: 'v6' },
  { value: 'v7', label: 'v7' }, { value: 'v8', label: 'v8' }, { value: 'v9', label: 'v9' },
  { value: 'v10', label: 'v10' },
];
const DEVICE_TYPES = ['mobile', 'tablet', 'desktop'];
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

interface LocalOffer { offer_id: string; position: number; weight: number }
interface LocalPath { _tempId: string; landing_page_id: string | null; weight: number; offers: LocalOffer[] }

/** Weight field with local state — only commits on blur. */
function WeightField({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [local, setLocal] = useState(String(value));
  useEffect(() => { setLocal(String(value)); }, [value]);
  return (
    <TextField
      type="number" size="small" value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { const n = parseInt(local, 10); if (!isNaN(n) && n >= 0 && n !== value) onSave(n); }}
      sx={{ width: 90, '& .MuiInputBase-root': { height: 40 }, '& input': { textAlign: 'center', fontSize: '0.85rem' } }}
      slotProps={{ input: { startAdornment: <Typography variant="caption" color="text.secondary" sx={{ mr: 0.3 }}>w:</Typography> } }}
    />
  );
}

function CountryFlag({ code }: { code: string }) {
  return (
    <Box component="img" loading="lazy" width="16" sx={{ mr: 0.5, verticalAlign: 'middle' }}
      src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`} alt={code} />
  );
}

function ConditionRow({ condition, onChange, onRemove }: {
  condition: RuleCondition;
  onChange: (c: RuleCondition) => void;
  onRemove: () => void;
}) {
  const [valueInput, setValueInput] = useState('');
  const isCountry = condition.field === 'country';
  const presetOptions = condition.field === 'device_type' ? DEVICE_TYPES
    : condition.field === 'weekday' ? WEEKDAYS : null;
  const countryLabel = (code: string) => {
    const name = COUNTRIES_MAP[code.toUpperCase()];
    return name ? `${code.toUpperCase()} - ${name}` : code.toUpperCase();
  };
  // Commit the typed value as a chip. Fires on Enter, on blur, and the + button, so mobile
  // (no reliable Enter key) can add values too. Guarded against blank/duplicate double-fires.
  const commitValue = () => {
    const v = valueInput.trim();
    if (v && !condition.values.includes(v)) onChange({ ...condition, values: [...condition.values, v] });
    setValueInput('');
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
      <Select size="small" value={condition.field}
        onChange={(e) => onChange({ ...condition, field: e.target.value, values: [] })}
        sx={{ minWidth: 120, height: 32, fontSize: '0.8rem' }}>
        {CONDITION_FIELDS.map(f => <MenuItem key={f.value} value={f.value} sx={{ fontSize: '0.8rem' }}>{f.label}</MenuItem>)}
      </Select>
      <Select size="small" value={condition.operator}
        onChange={(e) => onChange({ ...condition, operator: e.target.value as 'is' | 'is_not' })}
        sx={{ width: 80, height: 32, fontSize: '0.8rem' }}>
        <MenuItem value="is" sx={{ fontSize: '0.8rem' }}>is</MenuItem>
        <MenuItem value="is_not" sx={{ fontSize: '0.8rem' }}>is not</MenuItem>
      </Select>
      <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.3, alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.3, minHeight: 32 }}>
        {condition.values.map((v, i) => (
          <Chip key={i} icon={isCountry ? <CountryFlag code={v} /> : undefined}
            label={isCountry ? countryLabel(v) : v} size="small"
            onDelete={() => onChange({ ...condition, values: condition.values.filter((_, j) => j !== i) })}
            sx={{ height: 22, fontSize: '0.75rem' }} />
        ))}
        {isCountry ? (
          <Autocomplete size="small"
            options={COUNTRIES.filter(c => !condition.values.includes(c.code.toLowerCase()))}
            getOptionLabel={(o) => `${o.code} - ${o.label}`}
            value={undefined as never}
            onChange={(_, v) => { if (v) onChange({ ...condition, values: [...condition.values, v.code.toLowerCase()] }); }}
            blurOnSelect
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CountryFlag code={option.code} />{option.code} - {option.label}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} variant="standard" placeholder="+ country" size="small"
                sx={{ minWidth: 100, '& input': { fontSize: '0.75rem', py: 0 } }}
                slotProps={{ input: { ...params.InputProps, disableUnderline: true } }} />
            )}
            sx={{ flex: 1, minWidth: 100 }} />
        ) : presetOptions ? (
          <Select size="small" value="" displayEmpty variant="standard" disableUnderline
            onChange={(e) => { if (e.target.value) onChange({ ...condition, values: [...condition.values, String(e.target.value)] }); }}
            sx={{ minWidth: 90, fontSize: '0.75rem' }}>
            <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>+ add</MenuItem>
            {presetOptions.filter(o => !condition.values.includes(o)).map(o => (
              <MenuItem key={o} value={o} sx={{ fontSize: '0.75rem' }}>{o}</MenuItem>
            ))}
          </Select>
        ) : (
          <TextField variant="standard" size="small" placeholder="+ value" value={valueInput}
            onChange={(e) => setValueInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitValue(); } }}
            onBlur={commitValue}
            sx={{ minWidth: 90, '& input': { fontSize: '0.75rem', py: 0 } }}
            slotProps={{ input: {
              disableUnderline: true,
              endAdornment: valueInput.trim() ? (
                <IconButton size="small" aria-label="add value"
                  onMouseDown={(e) => e.preventDefault()} onClick={commitValue} sx={{ p: 0.2 }}>
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              ) : undefined,
            } }} />
        )}
      </Box>
      <IconButton size="small" color="error" onClick={onRemove} sx={{ p: 0.3 }}>
        <Delete sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

/**
 * Rule groups are edited here and nowhere else. A flow attaches a group; it never
 * rewrites one, so a flow save can't quietly change routing for the other flows
 * sharing it.
 */
export default function CreateEditRuleGroup() {
  const { modalState, setModal } = useModalState('rulegroup');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!modalState.id;

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [ref, setRef] = useState('');
  const [conditions, setConditions] = useState<RuleCondition[]>([]);
  const [paths, setPaths] = useState<LocalPath[]>([]);
  const [fallbackOfferId, setFallbackOfferId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState(defaultCreateWorkspace());

  const { data: group, isLoading } = useQuery({
    queryKey: ['rule-group', modalState.id],
    queryFn: () => flowsService.getRuleGroup(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });
  const { data: offers } = useQuery({ queryKey: ['offers-all'], queryFn: () => offersService.query({ limit: 500 }), enabled: modalState.open });
  const { data: landingPages } = useQuery({ queryKey: ['lps-all'], queryFn: () => landingPagesService.query({ limit: 500 }), enabled: modalState.open });

  useEffect(() => {
    if (!modalState.open) return;
    if (group && isEdit) {
      setName(group.name || '');
      setNotes(group.notes || '');
      setRef(group.ref || '');
      setConditions(group.conditions || []);
      setFallbackOfferId(group.fallback_offer_id ?? null);
      setPaths((group.paths || []).map(p => ({
        _tempId: crypto.randomUUID(),
        landing_page_id: p.landing_page_id,
        weight: p.weight,
        offers: (p.offers || []).map(o => ({ offer_id: o.offer_id, position: o.position, weight: o.weight })),
      })));
    } else if (!isEdit) {
      setName('');
      setNotes('');
      setRef('');
      setWorkspaceId(defaultCreateWorkspace());
      setConditions([]);
      setFallbackOfferId(null);
      setPaths([{ _tempId: crypto.randomUUID(), landing_page_id: null, weight: 100, offers: [] }]);
    }
    setError(null);
  }, [group, isEdit, modalState.open]);

  const close = () => setModal(undefined);
  const usedBy = group?.used_by_flow_count ?? 0;
  /**
   * The workspace fallback group is the global catch-all, served unconditionally. Conditions
   * on it would be accepted and then silently ignored, so the editor blocks them outright.
   */
  const isFallback = group?.is_workspace_fallback ?? false;

  const updatePath = (tempId: string, patch: Partial<LocalPath>) =>
    setPaths(prev => prev.map(p => p._tempId === tempId ? { ...p, ...patch } : p));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const fallback_offer_id = isFallback ? undefined : fallbackOfferId;
      const groupId = isEdit
        ? (await flowsService.editRuleGroup(modalState.id!, { name, conditions, ref, notes, fallback_offer_id })).id
        : (await flowsService.createRuleGroup({ name, conditions, ref, notes, fallback_offer_id, workspace_id: workspaceId })).id;

      // One atomic replace. Deleting then re-adding across several requests would
      // leave every flow sharing this group with no routing mid-save, and a failure
      // partway through would leave it half-written.
      const saved = await flowsService.replaceGroupPaths(groupId, paths.map(p => ({
        landing_page_id: p.landing_page_id,
        weight: p.weight,
        // Re-sequenced from array order: keeping stale positions lets a delete-then-add
        // produce duplicates ([1,2,3] -> delete 2 -> [1,3] -> add -> [1,3,3]), and the
        // server orders offers by position, so duplicates make the order ambiguous.
        offers: p.offers.map((o, i) => ({ offer_id: o.offer_id, position: i + 1, weight: o.weight ?? 100 })),
      })));

      // A group created from inside the flow editor should land ON that flow —
      // otherwise you are dropped back into the dialog having to search for the
      // thing you just made. The flow attaches it in local state and persists it
      // on ITS save, which also works for a flow that does not exist server-side yet.
      if (!isEdit) {
        window.dispatchEvent(new CustomEvent('rulegroup:created', { detail: saved }));
      }

      queryClient.invalidateQueries({ queryKey: ['rule-groups-library'] });
      queryClient.invalidateQueries({ queryKey: ['rule-group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      queryClient.invalidateQueries({ queryKey: ['flow'] });
      close();
      enqueueSnackbar(usedBy > 1 ? `Saved — applied to ${usedBy} flows` : 'Saved', { variant: 'success' });
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setError(err?.response?.data?.error?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DraggableDialog
      open={modalState.open}
      onClose={close}
      title={isEdit ? 'Edit Rule Group' : 'New Rule Group'}
      maxWidth="md"
      fullWidth
    >
      {isLoading && isEdit ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CopyId id={group?.id} show={isEdit} />
            </Box>

            {usedBy > 1 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Used by <b>{usedBy}</b> flows. Saving changes routing for all of them.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
              <TextField label="Group Name" size="small" value={name}
                onChange={(e) => setName(e.target.value)} sx={{ flex: 1 }} />
              <TextField label="Ref" size="small" value={ref} placeholder="e.g. R1200"
                onChange={(e) => setRef(e.target.value)} sx={{ width: { xs: '100%', sm: 160 } }} />
            </Box>
            {!isEdit && (
              <Box sx={{ mb: 2 }}>
                <WorkspaceField value={workspaceId} onChange={setWorkspaceId} />
              </Box>
            )}
            <TextField label="Notes" size="small" fullWidth multiline rows={2} value={notes}
            slotProps={{ input: { endAdornment: <NotesHistoryButton entity="rule-group" id={isEdit ? modalState.id : undefined} /> } }}
              onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }} />


            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Conditions</Typography>
            {isFallback ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This is the workspace fallback group — the global catch-all served when a flow's own
                groups match nothing. It must match every visitor, so it cannot take conditions.
              </Alert>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  All conditions must match. No conditions = matches everything (use as the fallback).
                </Typography>
                {conditions.map((c, i) => (
                  <ConditionRow key={i} condition={c}
                    onChange={(next) => setConditions(prev => prev.map((x, j) => j === i ? next : x))}
                    onRemove={() => setConditions(prev => prev.filter((_, j) => j !== i))} />
                ))}
                <Button size="small" startIcon={<Add />} sx={{ mb: 2 }}
                  onClick={() => setConditions(prev => [...prev, { field: 'country', operator: 'is', values: [] }])}>
                  Add Condition
                </Button>
              </>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Paths ({paths.length})</Typography>
            {paths.map((path) => (
              <Paper key={path._tempId} variant="outlined" sx={{ mb: 1, px: 1.5, py: 1, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 50, flexShrink: 0, fontSize: '0.7rem' }}>Lander</Typography>
                  <Autocomplete size="small"
                    options={(landingPages?.data || []).filter(lp => lp.status === 'active')}
                    getOptionLabel={(lp: LandingPage) => lp.name}
                    value={(landingPages?.data || []).find(lp => lp.id === path.landing_page_id) || null}
                    onChange={(_, v) => updatePath(path._tempId, { landing_page_id: v?.id || null })}
                    renderInput={(params) => <TextField {...params} placeholder="None (direct)" size="small" />}
                    sx={{ flex: 1 }} />
                  <WeightField value={path.weight} onSave={(weight) => updatePath(path._tempId, { weight })} />
                  <IconButton size="small" color="error" sx={{ p: 0.3 }}
                    onClick={() => setPaths(prev => prev.filter(p => p._tempId !== path._tempId))}>
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {path.offers.map((o, i) => (
                  <Box key={`${o.offer_id}-${i}`} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 50, flexShrink: 0, fontSize: '0.7rem' }}>Offer {i + 1}</Typography>
                    <Autocomplete size="small"
                      options={(offers?.data || []).filter(of => of.status === 'active')}
                      getOptionLabel={(of: Offer) => of.name}
                      value={(offers?.data || []).find(of => of.id === o.offer_id) || null}
                      onChange={(_, v) => {
                        if (v) updatePath(path._tempId, { offers: path.offers.map((x, j) => j === i ? { ...x, offer_id: v.id } : x) });
                        else updatePath(path._tempId, { offers: path.offers.filter((_, j) => j !== i) });
                      }}
                      renderInput={(params) => <TextField {...params} size="small" />}
                      sx={{ flex: 1, '& .MuiInputBase-root': { height: 32 } }} />
                    <IconButton size="small" color="error" sx={{ p: 0.3 }}
                      onClick={() => updatePath(path._tempId, { offers: path.offers.filter((_, j) => j !== i) })}>
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}

                <Autocomplete size="small"
                  options={(offers?.data || []).filter(of => of.status === 'active' && !path.offers.some(x => x.offer_id === of.id))}
                  getOptionLabel={(of: Offer) => of.name}
                  value={null}
                  onChange={(_, v) => {
                    if (v) updatePath(path._tempId, { offers: [...path.offers, { offer_id: v.id, position: path.offers.length + 1, weight: 100 }] });
                  }}
                  renderInput={(params) => <TextField {...params} size="small" placeholder="+ add offer" />}
                  sx={{ '& .MuiInputBase-root': { height: 32 } }} />
              </Paper>
            ))}
            <Button variant="outlined" startIcon={<Add />} fullWidth
              onClick={() => setPaths(prev => [...prev, { _tempId: crypto.randomUUID(), landing_page_id: null, weight: 100, offers: [] }])}>
              Add Path
            </Button>

            {!isFallback && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Fallback offer</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Used when none of this group's paths can take the click — e.g. every offer is capped or its
                  network closed. A capped lander keeps serving and its click-through goes here, so pick an
                  offer that accepts the lander's parameters. Empty = the workspace fallback.
                </Typography>
                <Autocomplete size="small"
                  options={(offers?.data || []).filter(of => of.status === 'active'
                    && of.workspace_id === (isEdit ? group?.workspace_id : workspaceId))}
                  getOptionLabel={(of: Offer) => of.name}
                  value={(offers?.data || []).find(of => of.id === fallbackOfferId) || null}
                  onChange={(_, v) => setFallbackOfferId(v?.id ?? null)}
                  renderInput={(params) => <TextField {...params} size="small" placeholder="Workspace fallback" />} />
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Close</Button>
            <Button variant="contained" onClick={save} disabled={saving || (!isEdit && !workspaceId)}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </>
      )}
    </DraggableDialog>
  );
}
